import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  AccessTime,
  Archive,
  CancelOutlined,
  CheckCircleOutlined,
  Delete,
  Edit,
  ErrorOutline,
  HourglassEmpty,
  ReportProblemOutlined,
  Unarchive
} from '@material-ui/icons';
import {
  resetValues,
  updatePageNo,
  updateRowsPerPage,
  updateSortBy,
  updateSortOrder
} from 'store/helpers/tableAndToolbar/actions';

const useStyles = makeStyles(theme => ({
  root: {},
  rootContent: {
    marginTop: theme.spacing(2)
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end',
    padding: '0px'
  },
  rightCol: {
    textAlign: 'right'
  },
  tableRow: {
    cursor: 'pointer'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '10%'
  },
  styleTable: {
    '& .MuiTableCell-sizeSmall': {
      padding: '2px 24px 2px 16px'
    }
  },
  toolTip: {
    fontSize: '0.8rem !important',
    '& .MuiTooltip-tooltip': {
      fontSize: '0.8rem !important'
    }
  },
  iconFont: {
    fontSize: '1.1rem'
  },
  hoverLabelDiv: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 'fit-content'
  },
  hoverIcon: {
    padding: '0px',
    paddingRight: '4px'
  },
  tableRowHeader: {
    height: '49px'
  },
  tableCell: {
    paddingTop: '12px !important',
    paddingBottom: '12px !important'
  },
  errorIcon: {
    color: theme.palette.error.main
  },
  warningIcon: {
    color: theme.palette.warning.main
  },
  linkDiv: {
    cursor: 'pointer',
  },
  disabledLinkDiv: {
    color: theme.palette.text.disabled,
    cursor: 'default',
    '&:hover': {
      color: theme.palette.text.disabled,
      textDecoration: 'none'
    }
  },
  noResultsDiv: {
    textAlign: 'center',
    padding: '16px',
    fontSize: '16px'
  }
}));
function getLinkData(tableTitle, getHoverLinkData, row) {
  return tableTitle.isHoverLink ? (
    getHoverLinkData(row, tableTitle)
  ) : (
    row[tableTitle.id]
  )
}

function rowFunction(row, tableTitle) {
  return row[tableTitle.id]
    ? row[tableTitle.id]
    : '-'
}
const TableWithIcons = props => {
  const {
    isLoading,
    tableTitles,
    onDoubleClickRow,
    actionButtonOptions,
    filteredRows,
    action,
    onClickLink,
    isLinkDisabled
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const rowsPerPage = useSelector(store => store.tableAndToolbar.rowsPerPage);
  const pageNo = useSelector(store => store.tableAndToolbar.pageNo);
  const totalCount = useSelector(store => store.tableAndToolbar.totalCount);
  const sortOrder = useSelector(store => store.tableAndToolbar.sortOrder);
  const sortBy = useSelector(store => store.tableAndToolbar.sortBy);

  const handlePageChange = (event, page) => {
    dispatch(updatePageNo(page));
    dispatch(action);
  };

  const handleRowsPerPageChange = event => {
    dispatch(updateRowsPerPage(event.target.value));
    dispatch(updatePageNo(0));
    dispatch(action);
  };

  const onClickTableSort = tableTitleId => {
    let orderBy = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    dispatch(updateSortOrder(tableTitleId === sortBy ? orderBy : 'ASC'));
    dispatch(updateSortBy(tableTitleId));
    dispatch(action);
  };

  const onDoubleClick = row => {
    if (onDoubleClickRow && typeof onDoubleClickRow === 'function') {
      onDoubleClickRow(row);
    }
  };

  const getActionIcons = row => {
    return (
      <>
        {actionButtonOptions(row).map(option => {
          return (
            <React.Fragment key={row.id + option.id}>
              {option.id === 'edit' && (
                <Tooltip title={option.title}>
                  <IconButton onClick={option.action}>
                    <Edit data-testid="editIcon" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {option.id === 'archive' && (
                <Tooltip title={option.title}>
                  <IconButton onClick={option.action}>
                    <Archive data-testid="archiveIcon" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {option.id === 'unarchive' && (
                <Tooltip title={option.title}>
                  <IconButton onClick={option.action}>
                    <Unarchive data-testid="unarchiveIcon" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {option.id === 'delete' && (
                <Tooltip title={option.title}>
                  <IconButton onClick={option.action}>
                    <Delete data-testid="deleteIcon" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const getIconButton = icon => {
    switch (icon) {
      case 'AccessTime':
        return <AccessTime className={classes.iconFont} color="secondary" />;
      case 'CancelOutlined':
        return (
          <CancelOutlined
            className={clsx(classes.iconFont, classes.errorIcon)}
          />
        );
      case 'HourglassEmpty':
        return (
          <HourglassEmpty className={classes.iconFont} color="secondary" />
        );
      case 'ReportProblemOutlined':
        return (
          <ReportProblemOutlined
            className={clsx(classes.iconFont, classes.warningIcon)}
          />
        );
      case 'CheckCircleOutlined':
        return (
          <CheckCircleOutlined className={classes.iconFont} color="primary" />
        );
      case 'ErrorOutline':
        return (
          <ErrorOutline className={clsx(classes.iconFont, classes.errorIcon)} />
        );
    }
  };

  const getHoverLinkData = (row, tableTitle) => {
    return (
      <Tooltip
        classes={{ tooltip: classes.toolTip }}
        arrow
        title={row.remarks || ''}
        placement="bottom-start">
        <div className={classes.hoverLabelDiv}>
          <IconButton size="medium" className={classes.hoverIcon}>
            {getIconButton(row.icon)}
          </IconButton>
          <Typography>{row[tableTitle.id]}</Typography>
        </div>
      </Tooltip>
    );
  };

  return (
    <Card className={clsx(classes.root, classes.rootContent)}>
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          {isLoading ? (
            <div className={classes.loader}>
              <CircularProgress className={classes.loader} />
            </div>
          ) : (
            <div className={classes.inner}>
              <Table size="small" className={classes.styleTable}>
                <TableHead>
                  <TableRow className={classes.tableRowHeader}>
                    {tableTitles.map(tableTitle => (
                      <React.Fragment key={tableTitle.id + 'row'}>
                        {tableTitle.id === 'actionButton' ? (
                          <TableCell
                            className={classes.tableCell}
                            key={tableTitle.id}
                          />
                        ) : (
                          <TableCell key={tableTitle.id}>
                            <TableSortLabel
                              active={tableTitle.id === sortBy}
                              direction={
                                tableTitle.id === sortBy
                                  ? sortOrder.toLowerCase()
                                  : 'asc'
                              }
                              onClick={() => onClickTableSort(tableTitle.id)}>
                              {tableTitle.label}
                            </TableSortLabel>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map(row => (
                    <TableRow
                      onDoubleClick={() => onDoubleClick(row)}
                      className={typeof onDoubleClickRow === 'function' ? classes.tableRow : ''}
                      hover
                      key={row.id}>
                      {tableTitles.map(tableTitle => (
                        <React.Fragment key={row.id + tableTitle.id}>
                          {tableTitle.id === 'actionButton' ? (
                            <TableCell className={classes.rightCol}>
                              {getActionIcons(row)}
                            </TableCell>
                          ) : (
                            <TableCell className={classes.tableCell}>
                              {tableTitle.isLink ? (
                                <Tooltip title={row.tooltipContent || ''} arrow>
                                  <Link
                                    className={
                                      isLinkDisabled
                                        ? classes.disabledLinkDiv
                                        : row[tableTitle.id] ? classes.linkDiv : ''
                                    }
                                    onClick={() =>
                                      onClickLink(
                                        row[tableTitle.id],
                                        row[tableTitle.linkId]
                                      )
                                    }>
                                    {
                                    rowFunction(row, tableTitle)
                                    }
                                  </Link>
                                </Tooltip>
                              ) : getLinkData(tableTitle, getHoverLinkData, row)}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </PerfectScrollbar>
        {!isLoading && totalCount === 0 && (
          <React.Fragment>
            <Typography className={classes.noResultsDiv}>
              No records found.
            </Typography>
            <Divider></Divider>
          </React.Fragment>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={pageNo}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
    </Card>
  );
};

TableWithIcons.propTypes = {
  isLoading: PropTypes.bool,
  tableTitles: PropTypes.array,
  onDoubleClickRow: PropTypes.func,
  actionButtonOptions: PropTypes.func,
  filteredRows: PropTypes.array,
  action: PropTypes.object,
  onClickLink: PropTypes.func
};

export default TableWithIcons;
